import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { TextStyle } from './TextStyle';

export const BannerVariations = [
  'error',
  'warn',
  'success',
  'critical',
  'info',
  'alert',
] as const;

export type BannerProps = {
  status: (typeof BannerVariations)[number];
  title?: string;
  children: ReactNode;
  className?: string;
};

export function Banner({ status, title, children, className }: BannerProps) {
  const isError = status === 'error';
  const isWarning = status === 'warn';
  const isInfo = status === 'info';
  const isAlert = status === 'alert';

  return (
    <section
      className={twMerge(
        'flex space-x-2 rounded-sm p-2',
        isError && 'bg-error-2',
        isInfo && 'bg-primary-2',
        isAlert && 'bg-warning-2',
        className
      )}
    >
      {Boolean(isError) && (
        <ExclamationTriangleIcon
          data-testid="bannerTextError"
          className="h-5 w-5 shrink-0 text-xs text-error-6"
        />
      )}

      {Boolean(isWarning) && (
        <ExclamationCircleIcon
          data-testid="bannerTextWarning"
          className="h-5 w-5 shrink-0 text-warning-6"
        />
      )}

      {Boolean(isInfo || isAlert) && (
        <InformationCircleIcon
          data-testid="bannerTextInfo"
          className={twMerge(
            'h-5 w-5 shrink-0 text-xs',
            isInfo && 'text-primary-6',
            isAlert && 'text-warning-6'
          )}
        />
      )}

      {title ? (
        <div>
          <TextStyle variant="base-medium">{title}</TextStyle>
          <p data-testid="bannerText" className="text-sm">
            {children}
          </p>
        </div>
      ) : (
        <p data-testid="bannerText" className="text-sm">
          {children}
        </p>
      )}
    </section>
  );
}
